import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setVariableDetails } from '../../redux/reducers/variables';
import { useReactFlow } from 'reactflow';
import styled, { css, keyframes } from 'styled-components';
import { RootState } from '../../redux/store';
import { handleWorkflowRun } from '../../services/method';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReactSVG } from 'react-svg';
import {
  Search, Play, ChevronRight, ArrowDownToLine, Settings2, Globe, GitBranch,
  GripVertical, AlertTriangle, Inbox, X, Copy, Check, Type as TypeIconGlyph,
  Hash, ToggleLeft, Calendar, Braces, Brackets, Fingerprint, Send, Clock, UserCircle2,
} from 'lucide-react';

interface VariablesPanelProps {
  data: any;
}

interface JsonTreeNode {
  key: string;
  value: any;
  path: string;
  type: string;
  isExpandable: boolean;
  children?: JsonTreeNode[];
  isFake?: boolean;
}

interface VariableInfo {
  path: string;
  type: string;
  value: any;
}

interface FieldItem {
  key: string;
  value: any;
  path: string;
  type: string;
  depth: number;
  isExpandable: boolean;
  index: string;
  isExpanded: boolean;
}

// Fields that are internal bookkeeping / pure UI cosmetics — never useful to
// reference in a text field, so they're excluded both from the tree shown to
// the user AND from the tracked-variables list (syntax highlighting).
const EXCLUDED_FIELDS = new Set([
  'icon', 'action', 'category', 'outputType', 'postions',
  'variablesRef', 'executeRun', 'childOnMinimize', 'childOnSettings',
  'isMediator', 'subCategory', 'currentCategory', 'isTemporaryMemory',
  'index', 'background', 'name'
]);

const isJsonString = (str: any): boolean => {
  if (typeof str !== 'string') return false;
  try { JSON.parse(str); return true; } catch { return false; }
};

const parseIfJson = (value: any): any => {
  if (typeof value === 'string' && isJsonString(value)) {
    try { return JSON.parse(value); } catch { return value; }
  }
  return value;
};

// Recursively prunes a tree down to only branches that match the query
// (by key or by leaf value), keeping ancestors of any match.
function filterTree(node: JsonTreeNode, query: string): JsonTreeNode | null {
  if (!query) return node;
  const q = query.toLowerCase();
  const selfMatches = node.key.toLowerCase().includes(q) || (!node.isExpandable && String(node.value ?? '').toLowerCase().includes(q));
  if (!node.children) return selfMatches ? node : null;
  const filteredChildren = node.children
    .map(c => filterTree(c, query))
    .filter((c): c is JsonTreeNode => c !== null);
  if (selfMatches || filteredChildren.length > 0) return { ...node, children: filteredChildren.length ? filteredChildren : node.children };
  return null;
}

function copyToClipboard(text: string) {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(text);
}

// Shows a clean "{{ path }}" pill as the drag ghost instead of the browser's
// default (which would drag a screenshot of the whole styled row).
function setPrettyDragImage(e: React.DragEvent, label: string) {
  const ghost = document.createElement('div');
  ghost.textContent = label;
  ghost.style.cssText = 'position:fixed;top:-999px;left:-999px;padding:6px 12px;background:#1d4ed8;color:#fff;font-family:Monaco,Menlo,monospace;font-size:12px;border-radius:8px;white-space:nowrap;box-shadow:0 6px 16px rgba(29,78,216,0.4);';
  document.body.appendChild(ghost);
  e.dataTransfer.setDragImage(ghost, 12, 14);
  requestAnimationFrame(() => document.body.removeChild(ghost));
}

const TYPE_ICON: Record<string, React.ElementType> = {
  string: TypeIconGlyph, number: Hash, boolean: ToggleLeft, date: Calendar, object: Braces, array: Brackets,
};
const TYPE_COLOR: Record<string, string> = {
  string: '#16a34a', number: '#2563eb', boolean: '#2563eb', date: '#7c3aed', array: '#9333ea', object: '#9333ea', undefined: '#9ca3af', null: '#9ca3af',
};

function TypeGlyph({ type }: { type: string }) {
  const Icon = TYPE_ICON[type];
  const color = TYPE_COLOR[type] || '#9ca3af';
  return Icon ? <TypeIconWrap style={{ color }}><Icon size={11} /></TypeIconWrap> : <Dot style={{ background: color }} />;
}

// A single draggable + click-to-copy leaf/branch chip. Shared by the tree
// renderer and the flat field renderer so both interactions (drag onto a
// text field, or click to copy the {{ path }}) behave identically everywhere.
function VarChip({ label, path, value, type, isMono }: { label: string; path: string; value: any; type: string; isMono?: boolean }) {
  const [copied, setCopied] = useState(false);
  return (
    <ChipRow
      draggable
      onDragStart={(e) => {
        setPrettyDragImage(e, `{{${path}}}`);
        e.dataTransfer.setData('application/reactflow/variable', JSON.stringify({ name: `{{${path}}}`, value, path, type, }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={() => { copyToClipboard(`{{${path}}}`); setCopied(true); setTimeout(() => setCopied(false), 1100); }}
      title="Drag into a field, or click to copy"
      $mono={isMono}
    >
      {label}
      <span className="copyFeedback">{copied ? <Check size={11} /> : <Copy size={11} />}</span>
    </ChipRow>
  );
}

const VariableNode = ({
  node, depth = 0, expandedPaths, toggleExpand, nodeId, forceExpand,
}: {
  node: JsonTreeNode;
  depth?: number;
  expandedPaths: Set<string>;
  toggleExpand: (path: string) => void;
  nodeId: string;
  forceExpand?: boolean;
}) => {
  const isExpanded = forceExpand || expandedPaths.has(node.path);

  const handleItemClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.isExpandable && !node.isFake) toggleExpand(node.path);
  }, [node.isExpandable, node.isFake, node.path, toggleExpand]);

  if (node.isFake && !node.path.includes('input-')) {
    return (
      <Row $depth={depth} $fake>
        <div className="rowHead">
          <span className="spacer" />
          <span className="name">{node.key}</span>
          <span className="hint">Run the workflow to see this</span>
        </div>
      </Row>
    );
  }

  return (
    <Row className="json-field-item" $depth={depth} $expandable={node.isExpandable}>
      <div className="rowHead" onClick={node.isExpandable ? handleItemClick : undefined}>
        {node.isExpandable ? (
          <button className="chevron" onClick={handleItemClick} type="button">
            <ChevronRight size={13} style={{ transform: isExpanded ? 'rotate(90deg)' : 'none' }} />
          </button>
        ) : <span className="spacer" />}

        {!node.isExpandable && <GripVertical size={12} className="grip" />}
        <TypeGlyph type={node.type} />

        {!node.isExpandable ? (
          <>
            <VarChip label={node.key} path={node.path} value={node.value} type={node.type} isMono />
            <span className="preview">{typeof node.value === 'string' ? node.value : String(node.value)}</span>
          </>
        ) : (
          <>
            <span className="name">{node.key}</span>
            {node.type === 'array' && Array.isArray(node.value) ? <span className="countBadge">{node.value.length}</span> : null}
          </>
        )}
      </div>

      {node.isExpandable && isExpanded && node.children?.length ? (
        <Reveal className="children">
          {node.children.map((child, i) => (
            <VariableNode key={i} node={child} depth={depth + 1} expandedPaths={expandedPaths} toggleExpand={toggleExpand} nodeId={nodeId} forceExpand={forceExpand} />
          ))}
        </Reveal>
      ) : null}
    </Row>
  );
};

const FieldRenderer = ({ field }: { field: FieldItem }) => (
  <Row className="json-field-item" $depth={0}>
    <div className="rowHead">
      <span className="spacer" />
      <GripVertical size={12} className="grip" />
      <TypeGlyph type={field.type} />
      <VarChip label={field.index} path={field.path} value={field.value} type={field.type} isMono />
      <span className="preview">{String(field.value)}</span>
    </div>
  </Row>
);

// Groups the (otherwise flat and repetitive) workflow-level fields into
// scannable categories instead of one long undifferentiated list.
const WORKFLOW_GROUPS: { label: string; icon: React.ElementType; keys: string[] }[] = [
  { label: 'Identity', icon: Fingerprint, keys: ['id', 'name', 'isLoop'] },
  { label: 'Export & API', icon: Send, keys: ['getExportApiUrl', 'getExportApiKey'] },
  { label: 'Timing', icon: Clock, keys: ['getLastRunDate', 'getDateOfCreation'] },
  { label: 'Owner', icon: UserCircle2, keys: ['getOwnerName', 'getOwnerEmail', 'getOwnerPlan'] },
];

export function VariablesPanel({ data }: VariablesPanelProps) {
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const { getNodes, getEdges, setNodes } = useReactFlow();
  const [isRunning, setIsRunning] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const project = useSelector((state: RootState) => state.project);
  const user = useSelector((state: RootState) => state.user.data);

  const handleRunPreviousNodes = useCallback(async () => {
    if (isRunning) return;
    setIsRunning(true);
    try {
      const allNodes = getNodes();
      const allEdges = getEdges();
      const currentNodeId = data.id;
      const relevantNodeIds = new Set<string>([currentNodeId]);
      const queue = [currentNodeId];
      const visited = new Set<string>([currentNodeId]);

      while (queue.length > 0) {
        const currentNodeId = queue.shift()!;
        const inputEdges = allEdges.filter(edge => edge.target === currentNodeId);
        for (const edge of inputEdges) {
          if (!visited.has(edge.source)) {
            visited.add(edge.source);
            relevantNodeIds.add(edge.source);
            queue.push(edge.source);
          }
        }
      }

      const relevantNodes = allNodes.filter(node => relevantNodeIds.has(node.id));
      const relevantEdges = allEdges.filter(edge => relevantNodeIds.has(edge.source) && relevantNodeIds.has(edge.target));
      const projectId = project.id;
      const userToken = user?.token || '';
      const apiKey = project.getExportAPIKey;

      await handleWorkflowRun(projectId, userToken, relevantNodes, relevantEdges, false, false, apiKey, dispatch, navigate, setNodes);
    } catch (error) {
      console.error('Error running previous nodes:', error);
    } finally {
      setIsRunning(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodes, getEdges, data.id, project.id, user?.token, dispatch, navigate, setNodes, isRunning]);

  const { nodes, connectedBlocksWithoutOutput } = React.useMemo(() => {
    const allNodes = JSON.parse(JSON.stringify(getNodes()));
    const allEdges = JSON.parse(JSON.stringify(getEdges()));
    const visited = new Set<string>();
    const stack = [data.id];

    while (stack.length) {
      const currentId = stack.pop()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      allEdges.filter((edge: any) => edge.target === currentId).forEach((edge: any) => {
        if (!visited.has(edge.source)) stack.push(edge.source);
      });
    }
    const relevantNodeIds = Array.from(visited);
    let nodes = allNodes.filter((node: any) => node.data.action !== 'output');
    nodes = nodes.map((node: any) => {
      if (!relevantNodeIds.includes(node.id)) {
        const { output, ...rest } = node.data;
        return { ...node, data: rest, isHideOutputFake: true };
      }
      return node;
    });
    const connectedBlocksWithoutOutput = nodes.filter((node: any) =>
      node.id !== data.id && !node.isHideOutputFake &&
      (!node.data.output || Object.keys(node.data.output || {}).length === 0) &&
      node.data.action !== 'input'
    );
    const connectionCountMap = new Map<string, number>();
    allEdges.forEach((edge: any) => {
      if (edge.target === data.id) connectionCountMap.set(edge.source, (connectionCountMap.get(edge.source) || 0) + 1);
    });
    nodes.sort((a: any, b: any) => {
      if (a.id === data.id) return -1;
      if (b.id === data.id) return 1;
      return (connectionCountMap.get(b.id) || 0) - (connectionCountMap.get(a.id) || 0);
    });
    return { nodes, connectedBlocksWithoutOutput };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNodes, getEdges, data.id, isRunning]);

  const toggleExpand = useCallback((path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  }, []);

  const buildJsonTree = React.useCallback((nodesData: any[]): JsonTreeNode[] => {
    if (!nodesData || !Array.isArray(nodesData)) return [];

    const traverse = (obj: any, path: string, nodeIndex: string): JsonTreeNode => {
      const parsed = parseIfJson(obj);
      const shouldParse = parsed !== obj;
      const isObject = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
      const isArray = Array.isArray(parsed);
      const isPrimitive = !isObject && !isArray;
      const prefixedPath = path ? `{{ $block.${nodeIndex}.${path} }}` : `{{ $block.${nodeIndex} }}`;

      if (isPrimitive) {
        return { key: path.split('.').pop() || nodeIndex, value: obj, path: prefixedPath, type: typeof obj, isExpandable: false };
      }

      const children: JsonTreeNode[] = [];
      const target = shouldParse ? parsed : obj;

      if (isArray) {
        target.forEach((item: any, i: number) => {
          const itemPath = path ? `${path}[${i}]` : `[${i}]`;
          children.push(traverse(item, itemPath, nodeIndex));
        });
      } else if (isObject) {
        Object.keys(target).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          const newPath = path ? `${path}.${key}` : key;
          children.push(traverse(target[key], newPath, nodeIndex));
        });
      }

      return { key: path ? path.split('.').pop() || nodeIndex : nodeIndex, value: obj, path: prefixedPath, type: isArray ? 'array' : 'object', isExpandable: true, children };
    };

    const result: JsonTreeNode[] = [];

    nodesData.forEach(node => {
      const nodeIndex = node.data?.index || 'unknown';
      const hasOutput = node.data?.output && Object.keys(node.data.output || {}).length > 0;

      const rootNode: JsonTreeNode = { key: nodeIndex, value: node.data, path: `{{ $block.${nodeIndex} }}`, type: 'object', isExpandable: true, children: [] };

      if (node.data && typeof node.data === 'object') {
        Object.keys(node.data).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          rootNode.children?.push(traverse(node.data[key], key, nodeIndex));
        });
      }

      // Show a "run this node" placeholder for any block (including the one whose panel
      // is currently open) that hasn't produced output yet, so you can always see what's
      // available — or what's missing — without switching to a different node.
      if (!hasOutput && node.data.action !== 'input' && !nodes.find((n: any) => n.id === node.id).isHideOutputFake) {
        rootNode.children?.push({ key: 'output', value: null, path: `{{ $block.${nodeIndex}.output }}`, type: 'object', isExpandable: false, isFake: true });
      }

      result.push(rootNode);
    });

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parseIfJson, data.id]);

  const getAllVariableDetails = React.useCallback((nodes: any[]): VariableInfo[] => {
    if (!nodes || !Array.isArray(nodes)) return [];

    const collectVariableDetails = (obj: any, currentPath: string, nodeIndex: string, details: VariableInfo[]) => {
      const parsed = parseIfJson(obj);
      const shouldParse = parsed !== obj;
      const target = shouldParse ? parsed : obj;
      const isObject = typeof target === 'object' && target !== null && !Array.isArray(target);
      const isArray = Array.isArray(target);
      const fullPath = currentPath ? `{{ $block.${nodeIndex}.${currentPath} }}` : `{{ $block.${nodeIndex} }}`;

      details.push({ path: fullPath, type: isObject ? 'object' : isArray ? 'array' : typeof target, value: target });

      if (isObject) {
        Object.keys(target).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          collectVariableDetails(target[key], currentPath ? `${currentPath}.${key}` : key, nodeIndex, details);
        });
      } else if (isArray) {
        target.forEach((item: any, index: number) => {
          collectVariableDetails(item, currentPath ? `${currentPath}[${index}]` : `[${index}]`, nodeIndex, details);
        });
      }
    };

    const allDetails: VariableInfo[] = [];
    nodes.forEach(node => {
      const nodeIndex = node.data?.index || 'unknown';
      allDetails.push({ path: `{{ $block.${nodeIndex} }}`, type: 'object', value: node.data });
      if (node.data && typeof node.data === 'object') {
        Object.keys(node.data).forEach(key => {
          if (EXCLUDED_FIELDS.has(key)) return;
          collectVariableDetails(node.data[key], key, nodeIndex, allDetails);
        });
      }
    });
    return allDetails;
  }, []);

  const jsonTree = React.useMemo(() => buildJsonTree(nodes), [buildJsonTree, nodes]);

  const filteredJsonTree = React.useMemo(() => {
    if (!search) return jsonTree;
    return jsonTree.map(n => filterTree(n, search)).filter((n): n is JsonTreeNode => n !== null);
  }, [jsonTree, search]);

  const globalFields = React.useMemo((): FieldItem[] => [{
    key: 'date', value: new Date().toUTCString(), path: `{{ $global.date }}`, type: 'date', depth: 0, isExpandable: false, index: 'Date', isExpanded: false,
  }], []);

  const workflowFields = React.useMemo((): FieldItem[] => [
    { key: 'id', value: project.id, path: `{{ $workflow.id }}`, type: 'string', depth: 0, isExpandable: false, index: 'id', isExpanded: false },
    { key: 'name', value: project.name, path: `{{ $workflow.name }}`, type: 'string', depth: 0, isExpandable: false, index: 'name', isExpanded: false },
    { key: 'isLoop', value: project.isLoop, path: `{{ $workflow.isLoop }}`, type: 'boolean', depth: 0, isExpandable: false, index: 'isLoop', isExpanded: false },
    { key: 'getExportApiUrl', value: project.getExportAPIURL, path: `{{ $workflow.getExportApiUrl }}`, type: 'string', depth: 0, isExpandable: false, index: 'getExportApiUrl', isExpanded: false },
    { key: 'getExportApiKey', value: project.getExportAPIKey, path: `{{ $workflow.getExportApiKey }}`, type: 'string', depth: 0, isExpandable: false, index: 'getExportApiKey', isExpanded: false },
    { key: 'getLastRunDate', value: project.getLastRunDate, path: `{{ $workflow.getLastRunDate }}`, type: 'string', depth: 0, isExpandable: false, index: 'getLastRunDate', isExpanded: false },
    { key: 'getDateOfCreation', value: project.getDateOfCreation, path: `{{ $workflow.getDateOfCreation }}`, type: 'string', depth: 0, isExpandable: false, index: 'getDateOfCreation', isExpanded: false },
    { key: 'getOwnerName', value: user?.fullName, path: `{{ $workflow.getOwnerName }}`, type: 'string', depth: 0, isExpandable: false, index: 'getOwnerName', isExpanded: false },
    { key: 'getOwnerEmail', value: user?.email, path: `{{ $workflow.getOwnerEmail }}`, type: 'string', depth: 0, isExpandable: false, index: 'getOwnerEmail', isExpanded: false },
    { key: 'getOwnerPlan', value: user?.plan?.subscription, path: `{{ $workflow.getOwnerPlan }}`, type: 'string', depth: 0, isExpandable: false, index: 'getOwnerPlan', isExpanded: false },
  ], [project, user]);

  const filteredGlobalFields = React.useMemo(() => !search ? globalFields : globalFields.filter(f => f.index.toLowerCase().includes(search.toLowerCase())), [globalFields, search]);
  const filteredWorkflowFields = React.useMemo(() => !search ? workflowFields : workflowFields.filter(f => f.index.toLowerCase().includes(search.toLowerCase())), [workflowFields, search]);
  const workflowFieldsByKey = React.useMemo(() => new Map(filteredWorkflowFields.map(f => [f.key, f])), [filteredWorkflowFields]);

  const allVariableDetails = React.useMemo(() => getAllVariableDetails(nodes), [getAllVariableDetails, nodes]);
  const allDetails = React.useMemo(() => [
    ...allVariableDetails,
    ...globalFields.map(f => ({ path: f.path, type: f.type, value: f.value })),
    ...workflowFields.map(f => ({ path: f.path, type: f.type, value: f.value }))
  ], [allVariableDetails, globalFields, workflowFields]);

  useEffect(() => { dispatch(setVariableDetails(allDetails)); }, [dispatch, allDetails, isRunning]);

  const hasResults = filteredJsonTree.length > 0 || filteredGlobalFields.length > 0 || filteredWorkflowFields.length > 0;

  return (
    <VariablesPanelContainer>
      <PanelHeader>
        <div className="titleRow">
          <div>
            <h2>Variables</h2>
            <p>Drag a value into a field, or click a chip to copy it</p>
          </div>
          <RunButton onClick={handleRunPreviousNodes} disabled={isRunning} className={isRunning ? 'running' : ''}>
            <Play size={13} fill="currentColor" />
            Run Previous Nodes
          </RunButton>
        </div>
        <SearchBox>
          <Search size={15} className="icon" />
          <input placeholder="Search variables…" value={search} onChange={e => setSearch(e.target.value)} />
          {search ? <button onClick={() => setSearch('')} type="button"><X size={14} /></button> : null}
        </SearchBox>
      </PanelHeader>

      {isRunning ? <Loader /> : (
        <PanelContent>
          {connectedBlocksWithoutOutput.length > 0 && (
            <Hint>
              <AlertTriangle size={15} className="hintIcon" />
              <div>
                <div className="hintTitle">Some blocks haven't run yet</div>
                <div className="hintDesc">Their output isn't available below until you run the workflow. Static configuration values still work.</div>
              </div>
            </Hint>
          )}

          {filteredJsonTree.length > 0 && (
            <VariableSection>
              <div className="sectionTitle">
                <GitBranch size={14} />
                <span>Block variables</span>
                <span className="count">{nodes.length}</span>
              </div>
              <div className="cards">
                {filteredJsonTree.map((node, i) => (
                  <BlockCard
                    key={i} node={node} nodeId={data.id}
                    expandedPaths={expandedPaths} toggleExpand={toggleExpand}
                    forceExpand={!!search}
                  />
                ))}
              </div>
            </VariableSection>
          )}

          {filteredGlobalFields.length > 0 && (
            <VariableSection>
              <div className="sectionTitle"><Globe size={14} /><span>Global variables</span></div>
              <div className="flatList">
                {filteredGlobalFields.map((field, index) => <FieldRenderer key={index} field={field} />)}
              </div>
            </VariableSection>
          )}

          {filteredWorkflowFields.length > 0 && (
            <VariableSection>
              <div className="sectionTitle"><Settings2 size={14} /><span>Workflow variables</span></div>
              <div className="groupedList">
                {WORKFLOW_GROUPS.map(group => {
                  const items = group.keys.map(k => workflowFieldsByKey.get(k)).filter((f): f is FieldItem => !!f);
                  if (!items.length) return null;
                  const GroupIcon = group.icon;
                  return (
                    <div className="group" key={group.label}>
                      <div className="groupLabel"><GroupIcon size={12} />{group.label}</div>
                      <div className="flatList">
                        {items.map((field, index) => <FieldRenderer key={index} field={field} />)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </VariableSection>
          )}

          {!hasResults && (
            <EmptyState>
              <Inbox size={32} className="icon" />
              <div className="title">{search ? 'No matching variables' : 'No output data yet'}</div>
              <div className="desc">{search ? 'Try a different search term.' : 'Execute the node to see available output fields here.'}</div>
            </EmptyState>
          )}
        </PanelContent>
      )}
    </VariablesPanelContainer>
  );
}

// A root block's card: real node icon + color (matching the canvas exactly),
// with children split into a prominent "Output" group and a secondary
// "Configuration" group, instead of one flat, unlabeled list.
function BlockCard({ node, nodeId, expandedPaths, toggleExpand, forceExpand }: {
  node: JsonTreeNode;
  nodeId: string;
  expandedPaths: Set<string>;
  toggleExpand: (path: string) => void;
  forceExpand: boolean;
}) {
  const accent = node.value?.background || '#64748b';
  const label = node.value?.name || node.key;
  const icon = node.value?.icon;
  const outputChild = node.children?.find(c => c.key === 'output');
  const otherChildren = node.children?.filter(c => c.key !== 'output') || [];

  const [outputOpen, setOutputOpen] = useState(true);
  const [configOpen, setConfigOpen] = useState(false);

  return (
    <Card>
      <div className="cardHead">
        <IconBadge style={{ background: accent }}>
          {typeof icon === 'string'
            ? (icon.endsWith('.png') || icon.endsWith('.jpg') ? <img src={icon} alt="" /> : <ReactSVG src={icon} />)
            : icon ? <FontAwesomeIcon icon={icon} /> : null}
        </IconBadge>
        <span className="label">{label}</span>
        <span className="indexTag">{node.key}</span>
      </div>

      {outputChild ? (
        <SubGroup>
          <button className="subHead" onClick={() => setOutputOpen(o => !o)} type="button">
            <ChevronRight size={12} style={{ transform: (outputOpen || forceExpand) ? 'rotate(90deg)' : 'none' }} />
            <ArrowDownToLine size={13} />
            <span>Output</span>
            <span className="subHint">what this block produced</span>
          </button>
          {(outputOpen || forceExpand) && (
            <Reveal className="subBody">
              {outputChild.isFake ? (
                <RunHint>Run this node to see its real output here.</RunHint>
              ) : outputChild.children?.length ? (
                outputChild.children.map((child, i) => (
                  <VariableNode key={i} node={child} depth={0} expandedPaths={expandedPaths} toggleExpand={toggleExpand} nodeId={nodeId} forceExpand={forceExpand} />
                ))
              ) : (
                <VariableNode node={outputChild} depth={0} expandedPaths={expandedPaths} toggleExpand={toggleExpand} nodeId={nodeId} forceExpand={forceExpand} />
              )}
            </Reveal>
          )}
        </SubGroup>
      ) : null}

      {otherChildren.length > 0 ? (
        <SubGroup>
          <button className="subHead" onClick={() => setConfigOpen(o => !o)} type="button">
            <ChevronRight size={12} style={{ transform: (configOpen || forceExpand) ? 'rotate(90deg)' : 'none' }} />
            <Settings2 size={13} />
            <span>Configuration</span>
            <span className="subHint">how this block was set up</span>
          </button>
          {(configOpen || forceExpand) && (
            <Reveal className="subBody">
              {otherChildren.map((child, i) => (
                <VariableNode key={i} node={child} depth={0} expandedPaths={expandedPaths} toggleExpand={toggleExpand} nodeId={nodeId} forceExpand={forceExpand} />
              ))}
            </Reveal>
          )}
        </SubGroup>
      ) : null}
    </Card>
  );
}

// ============================= Styles =============================

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Reveal = styled.div`
  animation: ${fadeSlideIn} 0.14s ease-out;
`;

const VariablesPanelContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 400px;
  height: 100vh;
  background-color: #f8fafc;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  z-index: 10000000;
  box-shadow: 4px 0 24px rgba(15, 23, 42, 0.08);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const PanelHeader = styled.div`
  padding: 20px 20px 16px;
  border-bottom: 1px solid #eef0f4;
  background-color: #fff;
  flex-shrink: 0;

  .titleRow {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
  }

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  p {
    margin: 3px 0 0;
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
    max-width: 190px;
  }
`;

const RunButton = styled.button`
  flex-shrink: 0;
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  border: none;
  padding: 8px 14px;
  border-radius: 9px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  box-shadow: 0 4px 10px -3px rgba(37, 99, 235, 0.4);
  transition: filter 0.15s ease, transform 0.15s ease;

  &:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { background: #cbd5e1; box-shadow: none; cursor: not-allowed; }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f1f3f7;
  border: 1px solid transparent;
  border-radius: 10px;
  padding: 8px 10px;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:focus-within { background: #fff; border-color: #93c5fd; }

  .icon { color: #94a3b8; flex-shrink: 0; }

  input {
    border: none;
    background: transparent;
    outline: none;
    font-size: 13px;
    color: #1e293b;
    width: 100%;
    &::placeholder { color: #94a3b8; }
  }

  button {
    border: none;
    background: #e2e8f0;
    color: #64748b;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    &:hover { background: #cbd5e1; }
  }
`;

const PanelContent = styled.div`
  flex: 1;
  padding: 18px 20px 28px;
  overflow-y: auto;
`;

const VariableSection = styled.div`
  margin-bottom: 24px;

  .sectionTitle {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12.5px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 10px;

    .count {
      margin-left: auto;
      background-color: #e0e7ff;
      color: #4338ca;
      padding: 1px 8px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 700;
      text-transform: none;
      letter-spacing: normal;
    }
  }

  .cards { display: flex; flex-direction: column; gap: 12px; }
  .flatList { display: flex; flex-direction: column; gap: 5px; }

  .groupedList { display: flex; flex-direction: column; gap: 14px; }
  .group .groupLabel {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-bottom: 6px;
  }
`;

const IconBadge = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #fff;
  font-size: 12px;

  img, svg { width: 13px; height: 13px; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.15s ease;
  &:hover { box-shadow: 0 4px 14px rgba(15, 23, 42, 0.07); }

  .cardHead {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 10px 12px;
    border-bottom: 1px solid #f1f3f7;

    .label { font-size: 13.5px; font-weight: 600; color: #1e293b; }
    .indexTag {
      margin-left: auto;
      font-family: 'Monaco', 'Menlo', monospace;
      font-size: 10.5px;
      color: #94a3b8;
      background: #f8fafc;
      border: 1px solid #eef0f4;
      padding: 1px 6px;
      border-radius: 5px;
    }
  }
`;

const SubGroup = styled.div`
  &:not(:last-child) { border-bottom: 1px solid #f1f3f7; }

  .subHead {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 9px 12px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: #334155;
    text-align: left;

    svg:first-child { color: #94a3b8; transition: transform 0.15s ease; flex-shrink: 0; }
    &:hover { background: #f8fafc; }

    .subHint {
      margin-left: auto;
      font-size: 10.5px;
      font-weight: 400;
      color: #94a3b8;
    }
  }

  .subBody { padding: 2px 8px 8px; display: flex; flex-direction: column; gap: 4px; }
`;

const RunHint = styled.div`
  font-size: 11.5px;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 10px;
  line-height: 1.4;
`;

const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const TypeIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ChipRow = styled.span<{ $mono?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: grab;
  padding: 2px 5px 2px 7px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  color: #1d4ed8;
  ${({ $mono }) => $mono && css`font-family: 'Monaco', 'Menlo', monospace;`}
  font-size: 11.5px;
  font-weight: 500;
  transition: all 0.15s ease;
  flex-shrink: 0;
  max-width: 150px;

  .copyFeedback {
    display: flex;
    align-items: center;
    color: #93c5fd;
    opacity: 0;
    transition: opacity 0.15s ease;
  }

  &:hover {
    background: #dbeafe;
    box-shadow: 0 2px 5px rgba(37, 99, 235, 0.25);
    transform: translateY(-1px);
    .copyFeedback { opacity: 1; }
  }
  &:active { cursor: grabbing; transform: translateY(0); }
`;

const Row = styled.div<{ $depth: number; $expandable?: boolean; $fake?: boolean }>`
  ${({ $depth }) => $depth > 0 && css`margin-left: ${$depth * 14}px;`}

  .rowHead {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 6px;
    border-radius: 7px;
    min-height: 28px;
    cursor: ${({ $expandable }) => ($expandable ? 'pointer' : 'default')};
    transition: background 0.12s ease;

    &:hover { background: #f8fafc; }

    .chevron {
      width: 16px; height: 16px; display: flex; align-items: center; justify-content: center;
      border: none; background: none; color: #94a3b8; cursor: pointer; flex-shrink: 0; padding: 0;
      svg { transition: transform 0.15s ease; }
    }
    .spacer { width: 16px; flex-shrink: 0; }
    .grip { color: #cbd5e1; flex-shrink: 0; }

    .name {
      font-size: 12.5px;
      font-weight: 500;
      color: #334155;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .preview {
      font-size: 11.5px;
      color: #94a3b8;
      font-family: 'Monaco', 'Menlo', monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      margin-left: 2px;
    }

    .countBadge {
      margin-left: 4px;
      font-size: 10.5px;
      font-weight: 700;
      color: #7c3aed;
      background: #f5f3ff;
      padding: 0 6px;
      border-radius: 8px;
    }

    .hint { margin-left: auto; font-size: 11px; color: #d97706; font-style: italic; }
  }

  ${({ $fake }) => $fake && css`
    .rowHead .name { color: #94a3b8; text-decoration: line-through; }
    opacity: 0.8;
  `}

  .children { margin-top: 2px; }
`;

const Hint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 11px 12px;
  margin-bottom: 18px;

  .hintIcon { color: #d97706; flex-shrink: 0; margin-top: 1px; }
  .hintTitle { font-weight: 600; font-size: 12.5px; color: #92400e; margin-bottom: 2px; }
  .hintDesc { font-size: 11.5px; color: #a16207; line-height: 1.45; }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 20px;
  color: #94a3b8;

  .icon { margin-bottom: 10px; opacity: 0.6; }
  .title { font-size: 14px; font-weight: 600; color: #475569; margin-bottom: 4px; }
  .desc { font-size: 12.5px; line-height: 1.5; max-width: 260px; }
`;

const Loader = styled.span`
  width: 56px;
  height: 56px;
  border: 4px solid #cbd5e1;
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 0.9s linear infinite;
  margin: 80px auto 0;

  @keyframes rotation {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
